import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CreateTicketDialog from 'components/common/CreateTicketDialog';
import MasterFilter, { useMasterFilter } from 'components/common/MasterFilter';
import type { FilterFieldConfig } from 'components/common/MasterFilter/types';
import CodeMirrorEditor from 'components/editors/CodeMirrorEditor';
import InlineFeedback from 'components/forms/molecules/InlineFeedback';
import useCurrentUser from 'hooks/useCurrentUser';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { Components } from 'react-markdown';
import Markdown from 'react-markdown';
import {
  useCreatePromptSectionMutation,
  useGetPromptSectionsQuery,
  useSuggestEditPromptSectionMutation,
} from 'services/api/promptSections.api';
import type { PromptSection } from 'services/types/promptSection';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_TINT,
  YELLOW,
} from 'styles/tokens';
import { getMutationErrorMessage } from 'utils/mutationError';

import NewPromptSectionButton from '../NewPromptSectionButton';

const mdComponents: Components = {
  h1: ({ children }) => (
    <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY, fontFamily: 'Jost, sans-serif', mt: 2, mb: 1 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="h6" sx={{ fontWeight: 600, color: NAVY, fontFamily: 'Jost, sans-serif', mt: 2, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 600, color: NAVY, fontFamily: 'Jost, sans-serif', mt: 1.5, mb: 0.5 }}
    >
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography variant="body2" sx={{ color: NAVY, fontFamily: 'Jost, sans-serif', lineHeight: 1.7, mb: 1 }}>
      {children}
    </Typography>
  ),
  li: ({ children }) => (
    <li>
      <Typography
        variant="body2"
        component="span"
        sx={{ color: NAVY, fontFamily: 'Jost, sans-serif', lineHeight: 1.7 }}
      >
        {children}
      </Typography>
    </li>
  ),
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 600, color: NAVY }}>
      {children}
    </Box>
  ),
  code: ({ children, className }) => {
    const isBlock = className?.startsWith('language-');
    if (isBlock) {
      return (
        <Box
          component="pre"
          sx={{
            bgcolor: SURFACE_TINT,
            borderRadius: RADIUS_ITEM,
            p: 1.5,
            my: 1,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            color: NAVY,
          }}
        >
          <code>{children}</code>
        </Box>
      );
    }
    return (
      <Box
        component="code"
        sx={{
          bgcolor: SURFACE_TINT,
          borderRadius: '4px',
          px: 0.5,
          fontFamily: 'monospace',
          fontSize: '0.85rem',
          color: NAVY,
        }}
      >
        {children}
      </Box>
    );
  },
};

const promptFilterConfig: FilterFieldConfig[] = [
  {
    key: 'search',
    kind: 'text',
    label: 'Buscar',
    placement: 'bar',
  },
  {
    key: 'permission',
    kind: 'singleSelect',
    label: 'Tipo',
    placement: 'bar',
    options: [
      { value: 'editable', label: 'Editables' },
      { value: 'read-only', label: 'Solo Lectura' },
      { value: 'suggest-edit', label: 'Sugerir Cambio' },
      { value: 'hidden', label: 'Oculta' },
    ],
  },
];

interface Props {
  businessUnitId: number;
  refetchFlag?: number;
}

const PromptBuilder: React.FC<Props> = ({ businessUnitId, refetchFlag = 0 }) => {
  const {
    data: promptSections,
    isLoading: loadingSections,
    refetch: refetchSections,
  } = useGetPromptSectionsQuery({ businessUnitId });
  const [createPromptSection] = useCreatePromptSectionMutation();
  const [suggestEditPromptSection] = useSuggestEditPromptSectionMutation();

  const currentUser = useCurrentUser();
  const filter = useMasterFilter(promptFilterConfig, businessUnitId);

  const [selectedSection, setSelectedSection] = useState<PromptSection | null>(null);
  const [editedValue, setEditedValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activatePromptVersion, setActivatePromptVersion] = useState(true);
  const [draftNotice, setDraftNotice] = useState(false);
  const [openCreateTicketDialog, setOpenCreateTicketDialog] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isAdmin = currentUser?.isAdmin ?? false;
  const availableSections =
    promptSections?.filter(
      (section) => section.isLastVersion && !section.isSuggestion && (isAdmin || section.permission !== 'hidden')
    ) || [];

  const searchTerm = (filter.values.search as string | undefined) ?? '';
  const permissionFilter = (filter.values.permission as string | undefined) ?? '';

  const filteredSections = useMemo(() => {
    let filtered = availableSections;
    if (searchTerm) {
      filtered = filtered.filter(
        (section) =>
          section.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          section.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          section.value.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (permissionFilter) {
      filtered = filtered.filter((section) => section.permission === permissionFilter);
    }
    return filtered;
  }, [availableSections, searchTerm, permissionFilter]);

  useEffect(() => {
    if (refetchFlag) {
      refetchSections().catch(console.error);
    }
  }, [refetchFlag, refetchSections]);

  useEffect(() => {
    if (selectedSection) {
      setEditedValue(selectedSection.value);
      setIsEditing(false);
    }
  }, [selectedSection]);

  const handleSectionSelect = (section: PromptSection) => {
    if (isEditing && editedValue !== selectedSection?.value) {
      const confirmDiscard = window.confirm('Tienes cambios sin guardar. ¿Deseas descartarlos?');
      if (!confirmDiscard) return;
    }
    setDraftNotice(false);
    setSelectedSection(section);
  };

  const handleStartEdit = () => {
    setActivatePromptVersion(true);
    setDraftNotice(false);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditedValue(selectedSection?.value || '');
    setIsEditing(false);
  };

  const handleSaveChanges = async () => {
    if (!selectedSection) return;
    setIsSaving(true);
    setSaveError(null);
    const isEditable = selectedSection.permission === 'editable';
    try {
      if (isEditable) {
        await createPromptSection({
          businessUnitId,
          promptSection: {
            name: selectedSection.name,
            displayName: selectedSection.displayName,
            value: editedValue,
            permission: selectedSection.permission,
            businessUnitId,
          },
          activatePromptVersion,
        }).unwrap();
      } else {
        await suggestEditPromptSection({
          businessUnitId,
          promptSection: {
            name: selectedSection.name,
            displayName: selectedSection.displayName,
            value: editedValue,
          },
        }).unwrap();
      }
      setIsEditing(false);
      setDraftNotice(isEditable && Boolean(selectedSection.isUsed) && !activatePromptVersion);
      const updatedSections = await refetchSections().unwrap();
      const updatedSection = updatedSections?.find(
        (s) => s.name === selectedSection.name && s.isLastVersion && !s.isSuggestion
      );
      if (updatedSection) setSelectedSection(updatedSection);
    } catch (error) {
      setSaveError(getMutationErrorMessage(error, 'No fue posible guardar los cambios.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleTicketCreated = () => setOpenCreateTicketDialog(false);

  const shouldShowTicketButton = currentUser && !currentUser.isAdmin && currentUser.role === 'businessUnitAdmin';

  if (loadingSections) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress size={60} sx={{ color: NAVY }} />
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Toolbar */}
      <Box sx={{ mb: { xs: 0, md: 2.5 } }}>
        <MasterFilter {...filter} />
        {shouldShowTicketButton && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setOpenCreateTicketDialog(true)}
              sx={{
                textTransform: 'none',
                borderRadius: RADIUS_COIN,
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                bgcolor: NAVY,
                color: '#fff',
                '&:hover': { bgcolor: NAVY, opacity: 0.9 },
              }}
            >
              Nuevo Ticket
            </Button>
          </Box>
        )}
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          display: 'flex',
          gap: 2.5,
          flex: 1,
          minHeight: 0,
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* Left Panel - Sections List */}
        <Box
          sx={{
            width: { xs: '100%', md: '35%' },
            height: { xs: 180, md: '100%' },
            display: 'flex',
            flexDirection: 'column',
            borderRadius: RADIUS_CARD,
            bgcolor: SURFACE_CONTAINER_LOWEST,
            boxShadow: SHADOW,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: { xs: 40, md: 52 },
              flexShrink: 0,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: NAVY,
                fontFamily: 'Jost, sans-serif',
                fontSize: { xs: '0.9rem', md: '1.25rem' },
              }}
            >
              Secciones de Prompt
            </Typography>
            <NewPromptSectionButton businessUnitId={businessUnitId} refetch={() => refetchSections()} />
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {filteredSections.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" sx={{ color: NAVY, opacity: 0.5, mb: 2, fontFamily: 'Jost, sans-serif' }}>
                  {searchTerm || permissionFilter
                    ? 'No se encontraron secciones con los filtros aplicados'
                    : 'No hay secciones disponibles'}
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {filteredSections.map((section, index) => {
                  const isSelected = selectedSection?.id === section.id;
                  const isOdd = index % 2 === 1;
                  return (
                    <ListItem key={section.id} sx={{ p: 0 }}>
                      <ListItemButton
                        onClick={() => handleSectionSelect(section)}
                        selected={isSelected}
                        sx={{
                          py: { xs: 1, md: 2 },
                          px: 2,
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: { xs: 0, md: 1 },
                          transition: 'all 0.2s ease',
                          bgcolor: isOdd ? SURFACE_TINT : 'transparent',
                          '&.Mui-selected': { bgcolor: SURFACE_CONTAINER_LOW },
                          '&:hover': { bgcolor: SURFACE_TINT },
                          '&.Mui-selected:hover': { bgcolor: SURFACE_CONTAINER_LOW },
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: { xs: 400, md: 600 },
                            fontSize: { xs: '0.85rem', md: '1rem' },
                            color: NAVY,
                            fontFamily: 'Jost, sans-serif',
                          }}
                        >
                          {section.displayName}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: NAVY,
                            opacity: 0.5,
                            fontSize: '0.8rem',
                            overflow: 'hidden',
                            display: { xs: 'none', md: '-webkit-box' },
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            width: '100%',
                            fontFamily: 'Jost, sans-serif',
                          }}
                        >
                          {section.value}
                        </Typography>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>

        {/* Right Panel - Section Editor */}
        <Box
          sx={{
            width: { xs: '100%', md: '65%' },
            minHeight: { xs: 400, md: 0 },
            height: { md: '100%' },
            display: 'flex',
            flexDirection: 'column',
            borderRadius: RADIUS_CARD,
            bgcolor: SURFACE_CONTAINER_LOWEST,
            boxShadow: SHADOW,
            overflow: 'hidden',
          }}
        >
          {selectedSection ? (
            <>
              <Box
                sx={{
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  height: { xs: 40, md: 52 },
                  flexShrink: 0,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: NAVY, fontFamily: 'Jost, sans-serif' }}>
                  {selectedSection.displayName}
                </Typography>
                {!isEditing && selectedSection.permission === 'hidden' && (
                  <Tooltip title="Esta sección sólo es visible para admins">
                    <Chip
                      label="Sección Oculta"
                      size="small"
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 600,
                        bgcolor: SURFACE_CONTAINER_LOW,
                        color: NAVY,
                        opacity: 0.7,
                      }}
                    />
                  </Tooltip>
                )}
                {!isEditing &&
                  selectedSection.permission !== 'read-only' &&
                  selectedSection.permission !== 'hidden' && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={handleStartEdit}
                      sx={{
                        textTransform: 'none',
                        borderRadius: RADIUS_COIN,
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 600,
                        color: NAVY,
                        borderColor: `rgba(21, 24, 58, 0.15)`,
                        '&:hover': { borderColor: NAVY, bgcolor: SURFACE_TINT },
                      }}
                    >
                      {selectedSection.permission === 'editable' ? 'Editar' : 'Sugerir Cambio'}
                    </Button>
                  )}
              </Box>
              {draftNotice && !isEditing && (
                <Alert
                  severity="info"
                  onClose={() => setDraftNotice(false)}
                  sx={{ mx: 2, mb: 1, borderRadius: RADIUS_ITEM }}
                >
                  Se guardó una nueva versión sin activarla: la sección sigue mostrando el contenido vigente. Actívala
                  desde el historial de versiones del prompt para aplicar el cambio.
                </Alert>
              )}
              {selectedSection.permission !== 'editable' && isEditing && (
                <Alert severity="info" sx={{ mx: 2, mb: 1, borderRadius: RADIUS_ITEM }}>
                  Los cambios se enviarán como sugerencia para revisión por un administrador.
                </Alert>
              )}
              {selectedSection.permission === 'editable' && selectedSection.isUsed && isEditing && (
                <Box sx={{ mx: 2, mb: 1, px: 1.5, py: 0.5, bgcolor: SURFACE_TINT, borderRadius: RADIUS_ITEM }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={activatePromptVersion}
                        onChange={(event) => setActivatePromptVersion(event.target.checked)}
                        sx={{ color: NAVY, '&.Mui-checked': { color: NAVY } }}
                      />
                    }
                    label="Guardar una nueva versión y activarla"
                    sx={{ color: NAVY, fontFamily: 'Jost, sans-serif' }}
                  />
                </Box>
              )}

              <Box
                sx={{
                  flex: 1,
                  m: 1.5,
                  mt: 0,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  overflow: 'hidden',
                  bgcolor: SURFACE_CONTAINER_LOW,
                  borderRadius: RADIUS_CARD,
                }}
              >
                {isEditing ? (
                  <>
                    {saveError && (
                      <InlineFeedback severity="error" sx={{ mb: 2 }}>
                        {saveError}
                      </InlineFeedback>
                    )}
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                      <CodeMirrorEditor
                        value={editedValue}
                        onChange={setEditedValue}
                        height="100%"
                        autoFocus
                        placeholder="Escribe el contenido de la sección..."
                        ariaLabel="Contenido de la sección de prompt"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={() => {
                          handleSaveChanges().catch(console.error);
                        }}
                        disabled={isSaving || editedValue === selectedSection.value}
                        sx={{
                          textTransform: 'none',
                          borderRadius: RADIUS_COIN,
                          fontFamily: 'Jost, sans-serif',
                          fontWeight: 600,
                          bgcolor: YELLOW,
                          color: NAVY,
                          '&:hover': { bgcolor: YELLOW, opacity: 0.85 },
                          '&.Mui-disabled': { bgcolor: `rgba(21, 24, 58, 0.08)`, color: `rgba(21, 24, 58, 0.3)` },
                        }}
                      >
                        {isSaving ? 'Guardando...' : 'Guardar Cambios'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                        sx={{
                          textTransform: 'none',
                          borderRadius: RADIUS_COIN,
                          fontFamily: 'Jost, sans-serif',
                          fontWeight: 600,
                          color: NAVY,
                          borderColor: `rgba(21, 24, 58, 0.15)`,
                          '&:hover': { borderColor: NAVY, bgcolor: SURFACE_TINT },
                        }}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ flex: 1, px: 2, py: 1, overflow: 'auto' }}>
                    <Markdown components={mdComponents}>{selectedSection.value}</Markdown>
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: NAVY, opacity: 0.5, mb: 2, fontFamily: 'Jost, sans-serif' }}>
                  Selecciona una seccion
                </Typography>
                <Typography variant="body2" sx={{ color: NAVY, opacity: 0.4, fontFamily: 'Jost, sans-serif' }}>
                  Haz clic en una seccion de la lista para ver y editar su contenido
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <CreateTicketDialog
        open={openCreateTicketDialog}
        onClose={() => setOpenCreateTicketDialog(false)}
        businessUnitId={businessUnitId}
        isAdmin={false}
        onSuccess={handleTicketCreated}
      />
    </Box>
  );
};

export default PromptBuilder;
